import { 
  Brain, 
  Code, 
  Palette, 
  Megaphone, 
  PenTool, 
  ShieldCheck, 
  Landmark, 
  Scale, 
  Users, 
  Wrench 
} from 'lucide-react';

export interface FreelancerProfile {
  id: string;
  name: string;
  avatarUrl: string;
  title: string;
  category: string;
  rating: number;
  reviewsCount: number;
  hourlyRate: number;
  badge: 'Top Rated Plus' | 'Top Rated' | 'Expert';
  skills: string[];
  description: string;
}

export interface FreelancerCategory {
  id: string;
  name: string;
  icon: any;
}

export const FREELANCER_CATEGORIES: FreelancerCategory[] = [
  { id: 'ai-services', name: 'AI Services', icon: Brain },
  { id: 'dev-it', name: 'Development & IT', icon: Code },
  { id: 'design-creative', name: 'Design & Creative', icon: Palette },
  { id: 'sales-marketing', name: 'Sales & Marketing', icon: Megaphone },
  { id: 'writing-translation', name: 'Writing & Translation', icon: PenTool },
  { id: 'admin-support', name: 'Admin & Support', icon: ShieldCheck },
  { id: 'finance-accounting', name: 'Finance & Accounting', icon: Landmark },
  { id: 'legal', name: 'Legal', icon: Scale },
  { id: 'hr-training', name: 'HR & Training', icon: Users },
  { id: 'engineering-arch', name: 'Engineering & Architecture', icon: Wrench }
];

export const BEST_FREELANCERS: FreelancerProfile[] = [
  // AI Services
  {
    id: 'ai-1',
    name: 'Alexander Vance',
    avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'PhD., AI Research & LLM Architect',
    category: 'ai-services',
    rating: 4.9,
    reviewsCount: 142,
    hourlyRate: 125,
    badge: 'Top Rated Plus',
    skills: ['PyTorch', 'OpenAI API', 'RAG', 'LangChain', 'Transformers', 'Python'],
    description: 'Specializes in tailoring enterprise-grade LLM agents, Retrieval-Augmented Generation (RAG) pipelines, and continuous pre-training frameworks with security constraints.'
  },
  {
    id: 'ai-2',
    name: 'Dr. Mei Chen',
    avatarUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Computer Vision & ML Deployment Specialist',
    category: 'ai-services',
    rating: 4.9,
    reviewsCount: 89,
    hourlyRate: 110,
    badge: 'Top Rated',
    skills: ['TensorRT', 'OpenCV', 'YOLOv8', 'Python', 'CNNs', 'Edge AI'],
    description: 'Expertise in high-throughput real-time object tracking, commercial vision pipelines, and deep optimization of learning weights for production edge devices.'
  },
  // Dev & IT
  {
    id: 'dev-1',
    name: 'Siddharth Mehta',
    avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Senior Full-Stack TypeScript Architect',
    category: 'dev-it',
    rating: 5.0,
    reviewsCount: 211,
    hourlyRate: 95,
    badge: 'Top Rated Plus',
    skills: ['React', 'Node.js', 'Next.js', 'PostgreSQL', 'Docker', 'AWS'],
    description: 'Developing high-concurrency Node and Next.js applications backstopped by performant SQL indexing and modern React component styling workflows.'
  },
  {
    id: 'dev-2',
    name: 'Elena Rostova',
    avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Kubernetes & Multi-Cloud DevOps Elite',
    category: 'dev-it',
    rating: 4.8,
    reviewsCount: 76,
    hourlyRate: 105,
    badge: 'Expert',
    skills: ['Kubernetes', 'Terraform', 'CI/CD', 'GKE', 'AWS', 'Security Hardening'],
    description: 'Securing multi-tenant cloud architectures via automated Terraform plans, Zero Trust Kubernetes network policies, and robust auto-scaling monitors.'
  },
  // Design & Creative
  {
    id: 'design-1',
    name: 'Marcello Rossi',
    avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Lead UI/UX Product Designer & Brand Specialist',
    category: 'design-creative',
    rating: 4.9,
    reviewsCount: 165,
    hourlyRate: 85,
    badge: 'Top Rated Plus',
    skills: ['Figma', 'Design Systems', 'Prototyping', 'Adobe CC', 'Interaction Design'],
    description: 'Creating delightful, high-fidelity user experiences and corporate design systems that drive double-digit conversions and brand coherence.'
  },
  {
    id: 'design-2',
    name: 'Chloe Lin',
    avatarUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Creative Art Director & UI Specialist',
    category: 'design-creative',
    rating: 5.0,
    reviewsCount: 94,
    hourlyRate: 90,
    badge: 'Top Rated',
    skills: ['Illustration', 'Sketch', 'Typography', 'Motion Design', 'Adobe Illustrator'],
    description: 'Fusing bespoke traditional media illustrations with modern minimalist vector layouts to produce unforgettable commercial brand identities.'
  },
  // Sales & Marketing
  {
    id: 'mkt-1',
    name: 'Sarah Jenkins',
    avatarUrl: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'B2B SaaS Growth Marketer & AdWords Lead',
    category: 'sales-marketing',
    rating: 4.9,
    reviewsCount: 120,
    hourlyRate: 80,
    badge: 'Top Rated Plus',
    skills: ['Copywriting', 'SEO', 'Google Analytics', 'CRM', 'Funnel Optimization'],
    description: 'Designing end-to-end user acquisition funnels, scaling premium search engine marketing campaigns, and reducing churn for series-A SaaS.'
  },
  {
    id: 'mkt-2',
    name: 'Raj Patel',
    avatarUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Cold Outreach Lead Generation Specialist',
    category: 'sales-marketing',
    rating: 4.8,
    reviewsCount: 68,
    hourlyRate: 75,
    badge: 'Expert',
    skills: ['Salesforce', 'HubSpot', 'Email Copywriting', 'LinkedIn Sales Navigator', 'Cold Calling'],
    description: 'Scraping qualified leads and executing hyper-personalized outbound sequences yielding consistent quarterly sales-qualified opportunities.'
  },
  // Writing & Translation
  {
    id: 'write-1',
    name: 'Sophia Sterling',
    avatarUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Senior API & Technical Documentation Writer',
    category: 'writing-translation',
    rating: 5.0,
    reviewsCount: 88,
    hourlyRate: 70,
    badge: 'Top Rated',
    skills: ['API Documentation', 'Ghostwriting', 'GitBook', 'Markdown', 'Technical Diagrams'],
    description: 'Transforming complex developer architectures and REST endpoints into clean, legible, and visual documentation that engineers enjoy reading.'
  },
  {
    id: 'write-2',
    name: 'Yuki Tanaka',
    avatarUrl: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'English-Japanese Localization Pro',
    category: 'writing-translation',
    rating: 4.9,
    reviewsCount: 54,
    hourlyRate: 65,
    badge: 'Expert',
    skills: ['Localization', 'Subtitling', 'Technical Translation', 'Editing', 'Software UI Tagging'],
    description: 'Translating and localizing high-risk medical software manuals, user interfaces, and mobile video game scripts with linguistic accuracy.'
  },
  // Admin & Support
  {
    id: 'adm-1',
    name: 'Liam O\'Connor',
    avatarUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Virtual Executive Assistant & Agile PM',
    category: 'admin-support',
    rating: 4.9,
    reviewsCount: 145,
    hourlyRate: 50,
    badge: 'Top Rated Plus',
    skills: ['Notion', 'Slack', 'Jira', 'Calendar Management', 'Email Triaging', 'Agile Scrum'],
    description: 'Synchronizing calendars, executing complex project tracking dashboards in Jira, and streamlining administrative communication channels for startup founders.'
  },
  {
    id: 'adm-2',
    name: 'Amina Diallo',
    avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'L2 Customer Success Lead & Zendesk Expert',
    category: 'admin-support',
    rating: 5.0,
    reviewsCount: 112,
    hourlyRate: 45,
    badge: 'Top Rated',
    skills: ['Zendesk', 'Intercom', 'Ticketing Resolution', 'Refund Policies', 'Customer Support'],
    description: 'Setting up automated ticketing macros, optimizing response queues, and boosting customer satisfaction (CSAT) ratings above 98%.'
  },
  // Finance & Accounting
  {
    id: 'fin-1',
    name: 'David Vance',
    avatarUrl: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'CPA & Financial Modeler for Tech Startups',
    category: 'finance-accounting',
    rating: 5.0,
    reviewsCount: 92,
    hourlyRate: 115,
    badge: 'Top Rated Plus',
    skills: ['Financial Modeling', 'Tax Compliance', 'Quickbooks', 'Startup Valuation', 'Excel Forecasting'],
    description: 'Drafting high-fidelity dynamic forecasting models for venture funding pitch decks, standardizing ledger entries, and filing clean startup corporate tax returns.'
  },
  {
    id: 'fin-2',
    name: 'Nisha Sharma',
    avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Senior Corporate Auditor & Bookkeeping Lead',
    category: 'finance-accounting',
    rating: 4.8,
    reviewsCount: 41,
    hourlyRate: 75,
    badge: 'Expert',
    skills: ['Auditing', 'Payroll Management', 'Xero', 'Standard Balances', 'Balance Sheets'],
    description: 'Resolving multi-year cash flow backlogs, reconciling balances, managing dynamic global payrolls, and structuring compliance files for annual audits.'
  },
  // Legal
  {
    id: 'leg-1',
    name: 'Sarah Jenkins, Esq.',
    avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'IP & Startup General Contract Attorney',
    category: 'legal',
    rating: 5.0,
    reviewsCount: 78,
    hourlyRate: 150,
    badge: 'Top Rated Plus',
    skills: ['Intellectual Property', 'SAAS Agreements', 'Terms of Service', 'Privacy Policy', 'Equity Granting'],
    description: 'Preparing solid terms of service agreements, master service agreements (MSAs), custom privacy notices, and custom early-stage shareholder vest agreements.'
  },
  {
    id: 'leg-2',
    name: 'Jean-Paul DuPont',
    avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'EU GDPR & Compliance Advisor',
    category: 'legal',
    rating: 4.9,
    reviewsCount: 39,
    hourlyRate: 130,
    badge: 'Top Rated',
    skills: ['Privacy Law', 'GDPR', 'Compliance Auditing', 'CCPA', 'Data Mapping'],
    description: 'Assessing technical platform infrastructure data-flow pipelines to ensure complete, bulletproof compliance with GDPR, CCPA, and European legal rulings.'
  },
  // HR & Training
  {
    id: 'hr-1',
    name: 'Dr. Karen Henderson',
    avatarUrl: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Global Talent Sourcing & Enterprise Recruiter',
    category: 'hr-training',
    rating: 4.9,
    reviewsCount: 82,
    hourlyRate: 90,
    badge: 'Top Rated Plus',
    skills: ['Corporate Sourcing', 'Candidate Screening', 'Onboarding', 'Corporate Training', 'L&D Systems'],
    description: 'Building custom software-guided onboarding workflows, designing executive corporate leadership courses, and scaling professional recruiter screening loops.'
  },
  {
    id: 'hr-2',
    name: 'Carlos Gomez',
    avatarUrl: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Technical Recruiter & HRIS Audit Specialist',
    category: 'hr-training',
    rating: 5.0,
    reviewsCount: 46,
    hourlyRate: 80,
    badge: 'Expert',
    skills: ['HRIS', 'BambooHR', 'LinkedIn Recruiter', 'Candidate Pipeline', 'Salaries Evaluation'],
    description: 'Conducting in-depth audits of existing HR database platforms, streamlining candidates funnel software integrations, and negotiating competitive executive offers.'
  },
  // Engineering & Architecture
  {
    id: 'eng-1',
    name: 'Amir Al-Zahrani',
    avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Mechanical Design Engineer & 3D CAD Expert',
    category: 'engineering-arch',
    rating: 4.9,
    reviewsCount: 65,
    hourlyRate: 100,
    badge: 'Top Rated',
    skills: ['SolidWorks', 'CAD', '3D Modeling', 'Finite Element Analysis (FEA)', 'Prototyping'],
    description: 'Designing consumer hardware enclosures, generating detailed engineering blueprints for optical components, and running finite element structural heat simulations.'
  },
  {
    id: 'eng-2',
    name: 'Lisa Mueller',
    avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Structural BIM Systems Engineer',
    category: 'engineering-arch',
    rating: 5.0,
    reviewsCount: 52,
    hourlyRate: 95,
    badge: 'Top Rated Plus',
    skills: ['Revit', 'AutoCAD', 'Structural Calculations', 'BIM Coordination', 'LEED Consultancy'],
    description: 'Coordinating complex multi-tier commercial building coordinates in Revit, auditing steel-girder loading strains, and advising on eco-friendly LEED standards.'
  },
  // Extra Premium AI Specialists
  {
    id: 'ai-3',
    name: 'Dr. Clara Dupont',
    avatarUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'PhD., Generative AI Trust & Safety Specialist',
    category: 'ai-services',
    rating: 5.0,
    reviewsCount: 42,
    hourlyRate: 135,
    badge: 'Top Rated Plus',
    skills: ['AI Alignment', 'MLOps', 'Red Teaming', 'Constitutional AI', 'Bias Mitigation'],
    description: 'Specializes in red-teaming production LLMs, developing alignment protocols, setting up automated bias mitigators, and preparing platforms for future AI regulatory regimes.'
  },
  {
    id: 'ai-4',
    name: 'Zackary Stone',
    avatarUrl: 'https://images.unsplash.com/photo-1501196354995-cbb51c65aaea?auto=format&fit=crop&w=120&h=120&q=80',
    title: ' Autonomous Multi-Agent Systems Engineer',
    category: 'ai-services',
    rating: 4.9,
    reviewsCount: 64,
    hourlyRate: 115,
    badge: 'Expert',
    skills: ['CrewAI', 'LangGraph', 'AutoGPT', 'Pinecone', 'Semantic Kernel'],
    description: 'Designing and deploying multi-agent AI workflows, vector-based search systems, and custom LLM integrations to automate complex business processes.'
  },
  // Extra Premium Dev & IT specialists
  {
    id: 'dev-3',
    name: 'Hassan Al-Fayeed',
    avatarUrl: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Smart Contract & Distributed Ledger Lead',
    category: 'dev-it',
    rating: 5.0,
    reviewsCount: 98,
    hourlyRate: 140,
    badge: 'Top Rated Plus',
    skills: ['Rust', 'Solidity', 'Zero-Knowledge Proofs', 'Smart Contract Audits', 'Go-Ethereum'],
    description: 'Architecting secure cryptographic locks, decentralized settlement protocols, and performant gas-optimized operations audited for multi-million dollar asset pools.'
  },
  {
    id: 'dev-4',
    name: 'Marcus Aurelius Vance',
    avatarUrl: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Zero-Trust Cloud Security & Compliance Consultant',
    category: 'dev-it',
    rating: 4.9,
    reviewsCount: 115,
    hourlyRate: 120,
    badge: 'Expert',
    skills: ['AWS Security', 'Penetration Testing', 'SOC2 Compliance', 'IAM Overhauls', 'WAF'],
    description: 'Hardening dynamic cloud networks, performing white-box network penetration, and managing formal SOC2/ISO-27001 readiness reviews with zero defects.'
  },
  // Extra Premium Design & Creative specialists
  {
    id: 'design-3',
    name: 'Aiko Tanaka',
    avatarUrl: 'https://images.unsplash.com/photo-1534751516642-a131ffd473fd?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Three.js & Interactive WebGL Designer',
    category: 'design-creative',
    rating: 4.9,
    reviewsCount: 53,
    hourlyRate: 100,
    badge: 'Top Rated',
    skills: ['Spline 3D', 'Three.js', 'WebGL', 'Figma', 'Interactive Storytelling'],
    description: 'Creating high-fidelity vector scenes and fluid responsive web layout experiences that feel interactive, dimensional, and perfectly adapted.'
  },
  {
    id: 'design-4',
    name: 'Lars Jansen',
    avatarUrl: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Lead SaaS Branding & System Architect',
    category: 'design-creative',
    rating: 5.0,
    reviewsCount: 132,
    hourlyRate: 95,
    badge: 'Top Rated Plus',
    skills: ['Figma Libraries', 'Brand Guidelines', 'Typography Pairing', 'Corporate Vectors'],
    description: 'Fusing rigid grid-based design standards with avant-garde brand identities to secure early-stage startup memorability and market authority.'
  },
  // Extra Sales/Marketing
  {
    id: 'mkt-3',
    name: 'Vanessa Carlton',
    avatarUrl: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Fractional CMO & Hubspot Revenue Architect',
    category: 'sales-marketing',
    rating: 4.9,
    reviewsCount: 47,
    hourlyRate: 90,
    badge: 'Top Rated',
    skills: ['HubSpot Certified', 'RevOps Mapping', 'Account-Based Marketing', 'Data Attribution'],
    description: 'Unifying loose corporate marketing and customer success modules into high-converting RevOps pipelines that eliminate lead leakage.'
  },
  // Extra Finance
  {
    id: 'fin-3',
    name: 'Gregory Sterling, CFA',
    avatarUrl: 'https://images.unsplash.com/photo-1500048993953-d23a436266cf?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Fractional CFO & Venture Capital Advisor',
    category: 'finance-accounting',
    rating: 5.0,
    reviewsCount: 112,
    hourlyRate: 160,
    badge: 'Top Rated Plus',
    skills: ['Cap Table Modeling', 'M&A Advisory', 'Tax Abatements', 'Series Funding Structure'],
    description: 'Providing premium boardroom financial advice, evaluating cap tables, structuring complex debt products, and representing companies in venture capital rounds.'
  },
  // Extra Legal
  {
    id: 'leg-3',
    name: 'Monica Geller, J.D.',
    avatarUrl: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Global FinTech Regulatory & Escrow Attorney',
    category: 'legal',
    rating: 5.0,
    reviewsCount: 61,
    hourlyRate: 175,
    badge: 'Top Rated Plus',
    skills: ['FinTech Escrow Law', 'Federal AML/KYC', 'SEC Compliance', 'Smart Contract Clauses'],
    description: 'Advising online marketplaces and escrow platforms on custody regulations, Anti-Money Laundering frameworks, and drafting secure, compliant contract templates.'
  },
  // Extra Writing & Translation
  {
    id: 'write-3',
    name: 'Jaanus Kross',
    avatarUrl: 'https://images.unsplash.com/photo-1489980508314-941910ded1f4?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Advanced SaaS Conversion Strategist & Copywriter',
    category: 'writing-translation',
    rating: 4.9,
    reviewsCount: 91,
    hourlyRate: 80,
    badge: 'Top Rated',
    skills: ['Direct Response Copy', 'Landing Page Layouts', 'SEO Clustering', 'Technical Ghostwriting'],
    description: 'Crafting authoritative landing page copy, complex blog hubs, and ghostwriting original whitepapers that translate readers directly into pipeline demo pipeline.'
  },
  // Extra Admin Support
  {
    id: 'adm-3',
    name: 'Samantha Fox',
    avatarUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'High-Performance Operations Assistant & Zapier Master',
    category: 'admin-support',
    rating: 5.0,
    reviewsCount: 187,
    hourlyRate: 55,
    badge: 'Top Rated Plus',
    skills: ['Custom Notion DBs', 'Zapier Automation', 'Calendly Matrices', 'Executive Correspondence'],
    description: 'Building custom corporate command interfaces in Notion, engineering multi-step Zapier automated loops, and shielding founder focus by triaging heavy inbound channels.'
  },
  // Extra Engineering
  {
    id: 'eng-3',
    name: 'Dr. Isabella Rossi',
    avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=120&h=120&q=80',
    title: 'Aerospace Structural Dynamics & CFD Lead',
    category: 'engineering-arch',
    rating: 5.0,
    reviewsCount: 30,
    hourlyRate: 125,
    badge: 'Top Rated Plus',
    skills: ['ANSYS Fluent', 'CFD Analytics', 'Aerodynamic Optimization', 'Composite Materials'],
    description: 'Consulting on aerodynamic lift ratios, executing finite volume fluid dynamics scans in ANSYS, and optimizing honeycomb strength-to-weight load matrices.'
  }
];

